import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Printer, FilePdf } from "@phosphor-icons/react";
import ResultsDashboard from "@/components/ResultsDashboard";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ScenarioDetail() {
  const { id } = useParams();
  const [scenario, setScenario] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/scenarios/${id}`);
        if (active) setScenario(data);
      } catch (e) {
        if (active) toast.error("Scenario not found");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [id]);

  const handlePrint = () => window.print();

  const handlePdf = async () => {
    if (!scenario) return;
    toast("Preparing PDF…");
    try {
      const { data: advisor } = await axios.get(`${API}/advisor`);
      const { buildPdfFromElement } = await import("@/lib/pdf");
      await buildPdfFromElement({
        elementId: "pdf-root",
        fileName: `${scenario.client_name.replace(/\s+/g, "-")}-breakdown.pdf`,
        advisor,
        clientName: scenario.client_name,
        documentTitle: scenario.business_name ? `Quick Breakdown · ${scenario.business_name}` : "Quick Breakdown",
      });
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("PDF generation failed", { description: e.message });
    }
  };

  if (loading) {
    return (
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="h-40 bg-white border border-brand-line rounded-md animate-pulse" />
      </main>
    );
  }

  if (!scenario) {
    return (
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <h1 className="font-serif text-3xl text-brand-forest">Scenario not found</h1>
        <Link to="/scenarios" className="mt-6 inline-flex items-center gap-2 text-brand-forest font-semibold">
          <ArrowLeft size={16} weight="bold" /> Back to scenarios
        </Link>
      </main>
    );
  }

  return (
    <main data-testid="scenario-detail-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8 no-print">
        <div>
          <Link
            to="/scenarios"
            className="inline-flex items-center gap-1.5 text-sm text-brand-muted hover:text-brand-forest transition-colors"
          >
            <ArrowLeft size={14} weight="bold" /> All scenarios
          </Link>
          <div className="mt-3 label-eyebrow">
            {scenario.business_name || "Client"} · saved{" "}
            {new Date(scenario.created_at).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" })}
          </div>
          <h1 className="mt-2 font-serif text-4xl md:text-5xl text-brand-forest leading-tight">
            {scenario.client_name}
          </h1>
          {scenario.notes && (
            <p className="mt-3 text-brand-ink/70 max-w-xl italic">"{scenario.notes}"</p>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handlePrint}
            data-testid="detail-btn-print"
            className="border-brand-line text-brand-forest hover:bg-brand-cream rounded-md gap-2"
          >
            <Printer size={16} weight="bold" /> Print
          </Button>
          <Button
            variant="outline"
            onClick={handlePdf}
            data-testid="detail-btn-pdf"
            className="border-brand-line text-brand-forest hover:bg-brand-cream rounded-md gap-2"
          >
            <FilePdf size={16} weight="bold" /> Download PDF
          </Button>
        </div>
      </div>
      <ResultsDashboard result={scenario.result} clientLabel={scenario.client_name} />
    </main>
  );
}
